import {
  QConfigQuestionType,
  useGetTagsByCategoryQuery,
  QuestionTagCategory,
} from '@exam-management/graphql';
import { Paper, Flex, Box, Text } from '@shared/components';
import { formatDateTime } from '@shared/utils';

import { SummaryCard } from '../../components';
import { useQConfigGeneratorDetails } from '../../providers/qconfig-generator-details-provider/use-qconfig-generator-details';

export default function InformationSummarySection() {
  const { qconfigGenerator } = useQConfigGeneratorDetails();
  const { data: getDifficultyTagsResult } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Difficulty } },
  });

  const { data: getCalculationTagsResult } = useGetTagsByCategoryQuery({
    variables: { input: { category: QuestionTagCategory.Calculation } },
  });
  const qbankStructures = qconfigGenerator?.qBankStructures?.map((qbs) => {
    return `${qbs.code}: ${qbs.name}`;
  });
  const difficultyMap = {
    hard: getDifficultyTagsResult?.qbsGetTagsByCategory.find((t) => t.name === 'ยาก')?.id ?? '',
    medium: getDifficultyTagsResult?.qbsGetTagsByCategory.find((t) => t.name === 'กลาง')?.id ?? '',
    easy: getDifficultyTagsResult?.qbsGetTagsByCategory.find((t) => t.name === 'ง่าย')?.id ?? '',
  };

  const creationDate = formatDateTime(qconfigGenerator?.latestExecution.createdAt);

  const calculationMap = {
    calculate:
      getCalculationTagsResult?.qbsGetTagsByCategory.find((t) => t.name === 'คำนวณ')?.id ?? '',
    nonCalculate:
      getCalculationTagsResult?.qbsGetTagsByCategory.find((t) => t.name === 'ไม่คำนวณ')?.id ?? '',
  };

  const singleQuestionTotal =
    qconfigGenerator?.metadata.conditions.questionTypes.find(
      (type) => type.type === QConfigQuestionType.Single,
    )?.needTotal ?? 0;
  const itemQuestionTotal =
    qconfigGenerator?.metadata.conditions.questionTypes.find(
      (type) => type.type === QConfigQuestionType.ItemSet,
    )?.needTotal ?? 0;

  const calculateQuestionTotal =
    qconfigGenerator?.metadata.conditions.tags.find(
      (tag) => tag.tag.id === calculationMap.calculate,
    )?.needTotal ?? 0;

  const nonCalculateQuestionTotal =
    qconfigGenerator?.metadata.conditions.tags.find(
      (tag) => tag.tag.id === calculationMap.nonCalculate,
    )?.needTotal ?? 0;

  const easyQuestionTotal =
    qconfigGenerator?.metadata.conditions.tags.find((tag) => tag.tag.id === difficultyMap.easy)
      ?.needTotal ?? 0;

  const mediumQuestionTotal =
    qconfigGenerator?.metadata.conditions.tags.find((tag) => tag.tag.id === difficultyMap.medium)
      ?.needTotal ?? 0;

  const hardQuestionTotal =
    qconfigGenerator?.metadata.conditions.tags.find((tag) => tag.tag.id === difficultyMap.hard)
      ?.needTotal ?? 0;

  const getQuestionPercentage = (input: number) => {
    if (qconfigGenerator.totalQuestions === 0) return 0;
    return Math.round((input / qconfigGenerator.totalQuestions) * 100);
  };

  return (
    <Paper>
      <Flex
        sx={(theme) => ({
          p: theme.spacing(6),
          flexDirection: 'column',
          gap: theme.spacing(6),
        })}
      >
        <Flex sx={(theme) => ({ gap: theme.spacing(4) })}>
          <SummaryCard
            title='จำนวนข้อสอบ'
            summaryItem={[
              {
                itemTitle: 'จำนวนทั้งหมด',
                itemValue: qconfigGenerator?.totalQuestions ?? 0,
                itemPercent: getQuestionPercentage(qconfigGenerator?.totalQuestions ?? 0),
              },
              {
                itemTitle: 'Single',
                itemValue: singleQuestionTotal,
                itemPercent: getQuestionPercentage(singleQuestionTotal),
              },
              {
                itemTitle: 'Item Set',
                itemValue: itemQuestionTotal,
                itemPercent: getQuestionPercentage(itemQuestionTotal),
              },
            ]}
          />
          <SummaryCard
            title='ประเภทข้อสอบ'
            summaryItem={[
              {
                itemTitle: 'คำนวณ',
                itemValue: calculateQuestionTotal,
                itemPercent: getQuestionPercentage(calculateQuestionTotal),
              },
              {
                itemTitle: 'ไม่คำนวณ',
                itemValue: nonCalculateQuestionTotal,
                itemPercent: getQuestionPercentage(nonCalculateQuestionTotal),
              },
            ]}
          />
          <SummaryCard
            title='ระดับความยาก'
            summaryItem={[
              {
                itemTitle: 'ง่าย',
                itemValue: easyQuestionTotal,
                itemPercent: getQuestionPercentage(easyQuestionTotal),
              },
              {
                itemTitle: 'กลาง',
                itemValue: mediumQuestionTotal,
                itemPercent: getQuestionPercentage(mediumQuestionTotal),
              },
              {
                itemTitle: 'ยาก',
                itemValue: hardQuestionTotal,
                itemPercent: getQuestionPercentage(hardQuestionTotal),
              },
            ]}
          />
        </Flex>
        <Flex
          sx={(theme) => ({
            gap: theme.spacing(2),
            flexDirection: 'column',
          })}
        >
          <Flex sx={{ gap: (theme) => theme.spacing(6) }}>
            <Box sx={{ width: '140px' }}>
              <Text>คลังข้อสอบ:</Text>
            </Box>
            <Text variant='body1Semibold'>{qbankStructures?.join('\n') ?? '-'}</Text>
          </Flex>
          <Flex sx={{ gap: (theme) => theme.spacing(6) }}>
            <Box sx={{ width: '140px' }}>
              <Text>Generated at:</Text>
            </Box>
            <Text variant='body1Semibold'>{creationDate}</Text>
          </Flex>
        </Flex>
      </Flex>
    </Paper>
  );
}
